/**
 * Styles for the PhotoGallery component.
 */

import { StyleSheet } from 'react-native';
import { color, space, text, icon, radius } from '../../../shared/tokens/mobile';

const GALLERY_HEIGHT = 180;
const PHOTO_WIDTH = 140;

export const styles = StyleSheet.create({
  heroEmpty: {
    height: GALLERY_HEIGHT,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: space.md,
    marginBottom: space.md,
    borderWidth: 2,
    borderColor: color.divider,
    borderStyle: 'dashed',
  },
  heroTitle: {
    fontSize: text.bodyMedium.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.primary,
    marginTop: space.sm,
  },
  heroSubtitle: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: space.xs,
  },
  heroUploadText: {
    fontSize: text.body.fontSize,
    color: color.primary,
    marginTop: space.sm,
  },

  galleryContainer: {
    marginBottom: space.sm,
  },
  galleryScroll: {
    paddingHorizontal: space.md,
    paddingVertical: space.xs,
  },

  photoCard: {
    width: PHOTO_WIDTH,
    height: GALLERY_HEIGHT - 30,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    marginRight: space.sm,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: color.divider,
  },
  photoThumbnail: {
    flex: 1,
    width: '100%',
    borderTopLeftRadius: radius.md,
    borderTopRightRadius: radius.md,
  },
  photoPlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: space.sm,
  },
  geoBadge: {
    position: 'absolute',
    top: space.xs,
    right: space.xs,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: color.success,
    alignItems: 'center',
    justifyContent: 'center',
  },
  captionBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingHorizontal: space.xs,
    paddingVertical: 3,
  },
  photoCaption: {
    fontSize: 10,
    color: '#ffffff',
  },

  addCard: {
    width: PHOTO_WIDTH,
    height: GALLERY_HEIGHT - 30,
    backgroundColor: color.surface,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: color.primary + '30',
    borderStyle: 'dashed',
  },
  addText: {
    fontSize: text.caption.fontSize,
    color: color.primary,
    fontWeight: text.bodyMedium.fontWeight,
    marginTop: space.xs,
  },

  countRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    paddingHorizontal: space.md,
    marginTop: space.xs,
  },
  countText: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
  },
  errorText: {
    fontSize: text.caption.fontSize,
    color: color.error,
    marginLeft: space.sm,
  },

  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  menuCard: {
    backgroundColor: color.surface,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    padding: space.lg,
    paddingBottom: space.xxl,
  },
  menuTitle: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
    marginBottom: space.lg,
    textAlign: 'center',
  },
  menuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: color.backgroundSecondary,
    marginBottom: space.sm,
  },
  menuOptionText: {
    marginLeft: space.md,
    flex: 1,
  },
  menuOptionTitle: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  menuOptionSubtitle: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: 2,
  },
  menuCancel: {
    marginTop: space.sm,
    padding: space.md,
    alignItems: 'center',
  },
  menuCancelText: {
    fontSize: text.body.fontSize,
    color: color.textMuted,
  },

  viewerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
  },
  viewerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: space.md,
    paddingTop: space.xxl,
    paddingBottom: space.md,
  },
  viewerClose: { padding: space.sm },
  viewerDelete: { padding: space.sm },
  viewerContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerImage: {
    width: '100%',
    height: '100%',
  },
  viewerPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerFooter: {
    padding: space.lg,
    paddingBottom: space.xxl,
  },
  viewerCaption: {
    fontSize: text.body.fontSize,
    color: '#ffffff',
    marginBottom: space.sm,
  },
  viewerMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.md,
  },
  viewerMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
  },
  viewerMetaText: {
    fontSize: text.caption.fontSize,
    color: 'rgba(255,255,255,0.7)',
  },

  confirmOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: space.xl,
    zIndex: 100,
  },
  confirmCard: {
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    width: '100%',
    maxWidth: 340,
  },
  confirmTitle: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
    marginBottom: space.xs,
  },
  confirmMessage: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
    textAlign: 'center',
    marginBottom: space.lg,
  },
  confirmActions: {
    flexDirection: 'row',
    gap: space.sm,
  },
  confirmBtn: {
    flex: 1,
    paddingVertical: space.sm + 2,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmCancelBtn: {
    backgroundColor: color.backgroundSecondary,
  },
  confirmCancelText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textSecondary,
  },
  confirmDeleteBtn: {
    backgroundColor: color.error,
  },
  confirmDeleteText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.surface,
  },
});
